import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, ShoppingBag, Minus, Plus, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function Cart() {
  const { t } = useI18n();
  const { items, remove, setQty, total, clear } = useCart();
  const { user } = useAuth();
  const [contact, setContact] = useState({ name: user?.name || "", email: user?.email || "" });
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, variant: i.variant, quantity: i.quantity })),
        origin_url: window.location.origin,
        name: contact.name, email: contact.email || undefined,
      };
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/loja" className="inline-flex items-center gap-1 text-th-blue hover:text-th-gold text-sm mb-6" data-testid="back-store"><ArrowLeft className="w-4 h-4" />{t("continue_shopping")}</Link>
      <h1 className="font-display font-bold text-3xl text-th-blue mb-8 flex items-center gap-3"><ShoppingBag className="w-7 h-7 text-th-gold" />{t("cart")}</h1>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200" data-testid="cart-empty">
          <p className="text-slate-500">{t("cart_empty")}</p>
          <Link to="/loja"><Button className="mt-4 bg-th-blue text-white">{t("continue_shopping")}</Button></Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-3" data-testid="cart-items">
            {items.map((i) => (
              <div key={i.key} data-testid={`cart-item-${i.key}`} className="bg-white rounded-xl p-4 border border-slate-200 flex gap-4 items-center">
                <img src={i.image_url} alt={i.name} className="w-16 h-16 rounded-lg object-cover" />
                <div className="flex-1">
                  <div className="font-medium text-th-blue">{i.name}</div>
                  {i.variant && <div className="text-xs text-slate-500">{i.variant}</div>}
                  <div className="text-sm text-th-gold font-semibold">{i.price.toFixed(2)} €</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="outline" className="h-8 w-8" data-testid={`qty-minus-${i.key}`} onClick={() => setQty(i.key, i.quantity - 1)}><Minus className="w-3 h-3" /></Button>
                  <span className="w-6 text-center">{i.quantity}</span>
                  <Button size="icon" variant="outline" className="h-8 w-8" data-testid={`qty-plus-${i.key}`} onClick={() => setQty(i.key, i.quantity + 1)}><Plus className="w-3 h-3" /></Button>
                </div>
                <Button size="icon" variant="ghost" className="text-red-500" data-testid={`remove-${i.key}`} onClick={() => remove(i.key)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-6 border border-th-gold/20 shadow-lg h-fit sticky top-24">
            <div className="flex justify-between text-lg font-display font-semibold text-th-blue mb-4">
              <span>{t("total")}</span><span data-testid="cart-total">{total.toFixed(2)} €</span>
            </div>
            <div className="space-y-3 mb-4">
              <div><Label>{t("form_name")}</Label><Input value={contact.name} data-testid="checkout-name" onChange={(e) => setContact({ ...contact, name: e.target.value })} className="mt-1" /></div>
              <div><Label>{t("form_email")}</Label><Input type="email" value={contact.email} data-testid="checkout-email" onChange={(e) => setContact({ ...contact, email: e.target.value })} className="mt-1" /></div>
            </div>
            <Button onClick={checkout} disabled={loading || !contact.name} data-testid="checkout-btn" className="w-full bg-th-gold text-th-bluedark hover:bg-th-goldlight font-semibold h-11">{loading ? t("loading") : t("checkout")}</Button>
            <p className="mt-3 text-xs text-slate-400 text-center">Stripe · Modo de teste · 4242 4242 4242 4242</p>
          </div>
        </div>
      )}
    </div>
  );
}
